import { useCallback, useEffect, useState } from "react";
import { ApiError, authApi } from "@/api/client";
import { clearSessionSnapshot, readSessionSnapshot, writeSessionSnapshot } from "@/offline/sessionCache";
import type { SessionUser } from "@/api/types";

/**
 * `unreachable` is NOT a flavour of `anonymous`, and the difference is the whole point.
 *
 * `authApi.me` already separates the two cleanly: a 401 RESOLVES to null — the server said
 * "not signed in" — while a network failure or a 5xx THROWS. So the catch below is exactly
 * the case where we could not ask, and mapping it to "anonymous" answered a question
 * nobody got to put.
 */
export type SessionStatus = "checking" | "authenticated" | "anonymous" | "unreachable";

export interface UseSessionResult {
  status: SessionStatus;
  user: SessionUser | null;
  /** Throws `AuthError` (bad credentials) or `ApiError` (network/server) — the
   *  caller (Login.tsx) is responsible for turning that into on-screen text. */
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

/**
 * Session state for the AUTHENTICATED app shell (Dashboard/Payroll/Reports/Capture) —
 * feature/auth's client-side half of "tenant comes from the session, never a header".
 *
 * `/entry` (QuickEntry.tsx) never uses this hook at all: it is a page App.tsx renders
 * BEFORE the shell, with no dependency on login. Everywhere else, `enabled` gates the
 * one network call this hook makes (`GET /api/auth/me`), so it only ever fires when
 * something is actually about to require a session — see App.tsx.
 */
export function useSession(enabled: boolean): UseSessionResult {
  const [status, setStatus] = useState<SessionStatus>(enabled ? "checking" : "anonymous");
  const [user, setUser] = useState<SessionUser | null>(null);

  useEffect(() => {
    if (!enabled) return;

    // Deliberately NO "already ran" ref guarding this effect (see useEntries.ts's
    // own remarks on the identical trap): React 18 StrictMode in dev mounts,
    // tears down, and remounts once. The teardown below aborts the FIRST fetch;
    // a guard that persisted across that remount (surviving because refs are not
    // reset by an unmount/remount pair) would block the SECOND effect run from
    // ever starting a fresh one, leaving `status` wedged on "checking" forever —
    // exactly what happened here before this fix (caught by a real Playwright
    // run against a live API, not by unit tests). The abort+restart below is
    // the correct, cheap behavior in both dev (StrictMode) and production
    // (mounts once, runs once).
    let cancelled = false;
    const controller = new AbortController();

    /**
     * KNOWN OFFLINE — never even attempt the request. Mirrors `TimeClock.tsx`'s own
     * job-codes effect (and `punch()`'s live-attempt check before it): a fetch that
     * cannot succeed has nothing to prove and only adds a browser-logged network error
     * to a screen that has to stay clean while offline
     * (`offline-punch.spec.ts`'s own AC9 gate — a reload while offline, immediately
     * before this effect would otherwise have fired, is exactly the case that caught
     * this same gap in the job-codes effect). Falls back to the identical "last known
     * good" answer the catch branch below already gives an unreachable network, so the
     * visible result is the same either way — only the doomed network attempt is gone.
     * @ui-check: this must stay reachable with no live API — the offline-reload path
     * (AC8) depends on it never attempting a request while `navigator.onLine` is false.
     */
    function resolveKnownOffline(): void {
      const cached = readSessionSnapshot();
      if (cached) {
        setUser(cached);
        setStatus("authenticated");
        return;
      }
      setStatus("unreachable");
    }

    async function attempt(): Promise<void> {
      await authApi
        .me(controller.signal)
        .then((resolved) => {
          if (cancelled) return;
          setUser(resolved);
          setStatus(resolved ? "authenticated" : "anonymous");
          // A REACHABLE answer, either way — the server said "yes, this is who you are"
          // or "no, that cookie doesn't work". Both are authoritative, so both are the
          // right moment to update what a future offline reload trusts (ef-017 AC8).
          if (resolved) writeSessionSnapshot(resolved);
          else clearSessionSnapshot();
        })
        .catch((err: unknown) => {
          if (cancelled) return;
          // NOT "anonymous". The previous comment here read: "falling back to 'anonymous'
          // always has a safe next step (the login form), where staying on 'checking'
          // forever would not." The first half of that is right — a permanent spinner is
          // the worse failure — and the second half was measured and is FALSE.
          //
          // MEASURED 2026-09-06, API stopped so Caddy answered 502:
          //
          //     the laptop dropped to the sign-in form, mid-session
          //     POST /api/auth/login -> 502   (probed with a deliberately wrong password)
          //
          // The login form is not a next step during an outage; it is a dead end that also
          // implies the operator's session ended when it had not. At the booth that is
          // Christy's laptop asking for a password in front of a prospect, for a sign-in
          // that is still perfectly valid — and the password would not work either.
          //
          // The session DOES survive: once the API returned, a reload put her straight back
          // on the payroll screen without signing in again. So the honest state is "we could
          // not ask", which is what this now says.
          console.warn("Could not check session status.", err);

          // NARROWED after reviewing this diff against code-quality.md §6's checklist.
          // `authApi.me` throws for ANY non-401 failure, which includes a 403 — and a 403 is
          // the server ANSWERING, not failing to. Calling that "unreachable" would put
          // "Nothing has been signed out" on screen as a fresh false claim, which is the
          // exact habit the rest of this change removes.
          //
          // `neverReachedTheServer` is this codebase's own predicate for "the request never
          // reached the application" (0 / 502 / 504 — api/client.ts deliberately excludes
          // 503, because a 503 can come from the app itself). The 403 reasoning above is right
          // and unchanged.
          //
          // ⚠️ IT WAS ONE STATUS TOO TIGHT, AND A 500 FELL THROUGH TO "anonymous".
          //
          // MEASURED 2026-09-07 by stopping postgres under a signed-in operator: with a real
          // session cookie, `GET /api/auth/me` returns **500**. The server is not claiming the
          // session is invalid — it is saying it could not check. The request DID reach the
          // application, so `neverReachedTheServer` is false, so this branch called it
          // `anonymous` and App.tsx rendered the sign-in form.
          //
          // That is the exact dead end the `unreachable` state was written to prevent, arriving
          // through the door the predicate left open: navigating to Reports during a blip threw
          // the operator to a password prompt in front of a prospect — and she could not sign
          // back in, because login needs the same database. A 403 is the server ANSWERING; a
          // 5xx is the server saying it could not.
          if (err instanceof ApiError && (err.neverReachedTheServer || err.status >= 500)) {
            // ef-017 AC8 — "could not ask" is exactly the case a phone reloaded with no
            // signal hits every time, and the worker (or owner) sitting on it a moment
            // ago is still the same person a moment later. Prefer the last REACHABLE
            // answer this device got over blocking on one it cannot get right now — the
            // same "last known good" reasoning `referenceCache.ts` already applies to
            // vendors and categories, one layer up. Absent a cache (this device has
            // never successfully signed in), there is nothing to trust and the
            // `unreachable` screen is still the honest answer.
            const cached = readSessionSnapshot();
            if (cached) {
              setUser(cached);
              setStatus("authenticated");
              return;
            }
            setStatus("unreachable");
            return;
          }
          setStatus("anonymous");
        });
    }

    /**
     * The one entry point this effect (and the `online` retry below) ever calls —
     * `navigator.onLine` decides between the two, so nothing here can ever launch a
     * fetch it already knows will fail.
     */
    function check(): void {
      if (!navigator.onLine) {
        resolveKnownOffline();
        return;
      }
      void attempt();
    }

    check();
    // The device just regained the signal `check()` refused to spend on a doomed
    // request — ask for real now. Same 'online' retrigger `drainTriggers.ts` and
    // `TimeClock.tsx`'s job-codes effect already use for the identical reason.
    window.addEventListener("online", check);

    return () => {
      cancelled = true;
      controller.abort();
      window.removeEventListener("online", check);
    };
  }, [enabled]);

  const login = useCallback(async (email: string, password: string) => {
    const resolved = await authApi.login(email, password);
    setUser(resolved);
    setStatus("authenticated");
    writeSessionSnapshot(resolved);
  }, []);

  const logout = useCallback(async () => {
    await authApi.logout();
    setUser(null);
    setStatus("anonymous");
    clearSessionSnapshot();
  }, []);

  return { status, user, login, logout };
}
